import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import { aggregatorsApi, AggregatorRow, Page } from '../../api/aggregators';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';
import { AggregatorDrawer } from './AggregatorDrawer';
import { date, naira, sectionTitle, StatusBadge, td, th } from './shared';

export const AggregatorsPanel: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const toast = useToast();
  const [data, setData] = useState<Page<AggregatorRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(
        await aggregatorsApi.list({ q: search || undefined, status: status || undefined, page, limit: 25 }),
      );
    } catch (err) {
      toast.error(err, 'Failed to load aggregators.');
    } finally {
      setLoading(false);
    }
  }, [search, status, page, toast]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const close = useCallback(() => setOpenId(null), []);

  return (
    <Card padding={20}>
      <h3 style={sectionTitle}>Aggregators</h3>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(query.trim());
        }}
        style={{ display: 'flex', gap: 8, margin: '12px 0 20px', flexWrap: 'wrap' }}
      >
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 420 }}>
          <Search
            size={15}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--neutral-500)',
            }}
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, email or phone"
            style={{
              width: '100%',
              height: 38,
              padding: '0 12px 0 34px',
              fontSize: 13,
              fontFamily: 'var(--font-family)',
              color: 'var(--neutral-800)',
              background: 'var(--neutral-100)',
              border: '1px solid var(--neutral-300)',
              borderRadius: 'var(--radius-md)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          style={{
            height: 38,
            padding: '0 10px',
            fontSize: 13,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--neutral-300)',
            background: 'var(--neutral-100)',
            color: 'var(--neutral-800)',
          }}
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="revoked">Revoked</option>
        </select>
        <Button type="submit" variant="secondary">
          Search
        </Button>
      </form>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      ) : !data?.items.length ? (
        <EmptyState
          title={search || status ? 'No aggregators match.' : 'No aggregators yet.'}
          description={search || status ? undefined : 'Approve an application to add the first one.'}
        />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Aggregator', 'Status', 'Share', 'Businesses', 'Earned', 'Since', ''].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>{a.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>{a.email ?? a.phone}</div>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <StatusBadge status={a.status} />
                        {a.withdrawalsPaused ? <Badge variant="warning">Withdrawals paused</Badge> : null}
                        {a.flaggedBusinesses ? (
                          <Badge variant="warning">
                            {a.flaggedBusinesses} flagged
                          </Badge>
                        ) : null}
                      </div>
                    </td>
                    <td style={td}>{a.commissionPercent}%</td>
                    <td style={td}>{a.businessCount}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(a.earned)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--neutral-500)' }}>{date(a.approvedAt)}</td>
                    <td style={td}>
                      <Button variant="ghost" onClick={() => setOpenId(a.id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={data.meta.page}
            totalPages={data.meta.totalPages}
            total={data.meta.total}
            onPageChange={setPage}
          />
        </>
      )}

      <AggregatorDrawer id={openId} onClose={close} onChanged={() => void load()} />
    </Card>
  );
};
